import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import QRCodeStyling from 'qr-code-styling';
import {
  Copy, ArrowsClockwise, Play, Stop, Eye, Sliders, Broadcast,
  MusicNote, Timer, ListNumbers, ChartBar, VideoCamera, CheckSquare, QrCode, MicrophoneStage
} from '@phosphor-icons/react';

const FRONTEND_URL = window.location.origin;

function CopyButton({ text, label }) {
  const copy = () => {
    navigator.clipboard.writeText(text);
    toast.success(`${label || 'URL'} disalin!`);
  };
  return (
    <button onClick={copy} className="inline-flex items-center gap-1 border-2 border-foreground bg-secondary px-2 py-1 text-[10px] font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]">
      <Copy size={10} weight="bold" /> Copy
    </button>
  );
}

function DashQRPreview({ accent }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const qr = new QRCodeStyling({
      width: 150,
      height: 150,
      type: 'canvas',
      data: `${FRONTEND_URL}/u/preview`,
      image: '/logo-favicon.png',
      margin: 4,
      qrOptions: { errorCorrectionLevel: 'H' },
      imageOptions: { 
        crossOrigin: 'anonymous', 
        margin: 0, 
        imageSize: 0.6 
      },
      dotsOptions: { type: 'rounded', color: '#1a1a1a' },
      cornersSquareOptions: { type: 'extra-rounded', color: '#000000' },
      cornersDotOptions: { type: 'dot', color: '#000000' },
      backgroundOptions: { color: '#FFFFFF' },
    });
    ref.current.innerHTML = '';
    qr.append(ref.current);
  }, [accent]);
  return <div ref={ref} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} />;
}

export default function DashOverlay() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [activeVote, setActiveVote] = useState(null);
  const [voteTitle, setVoteTitle] = useState('');
  const [voteOptions, setVoteOptions] = useState('');
  const [subathonDuration, setSubathonDuration] = useState(60);
  const [subathonActive, setSubathonActive] = useState(false);
  const [mediaQueueCount, setMediaQueueCount] = useState(0);
  const [qrLabel, setQrLabel] = useState('TRAKTIR DI SINI');
  const [sseStatus, setSseStatus] = useState('connecting');
  const [testingAlert, setTestingAlert] = useState(false);
  const [testingMedia, setTestingMedia] = useState(false);
  const [testMediaUrl, setTestMediaUrl] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.get('/overlay/config');
      setConfig(res.data);
      setForm(res.data);
    } catch (err) {
      toast.error('Gagal memuat konfigurasi overlay');
    } finally { setLoading(false); }
  }, []);

  const loadVote = useCallback(async () => {
    try {
      const res = await api.get('/overlay/vote/active');
      setActiveVote(res.data?.vote_id ? res.data : null);
    } catch {}
  }, []);

  const loadSubathon = useCallback(async () => {
    try {
      const res = await api.get(`/overlay/subathon/status_by_user`); // Need to add this endpoint or just use config
      setSubathonActive(res.data?.active || false);
    } catch {}
  }, []);

  const loadMediaQueue = useCallback(async () => {
    try {
      const res = await api.get(`/overlay/media/count_by_user`); // Need to add this endpoint
      setMediaQueueCount(res.data?.count || 0);
    } catch {}
  }, []);

  useEffect(() => { loadConfig(); loadVote(); loadSubathon(); loadMediaQueue(); }, [loadConfig, loadVote, loadSubathon, loadMediaQueue]);

  const token = config?.overlay_token || '';

  // Sync dashboard with live events
  useEffect(() => {
    if (!token) return;
    const url = `${api.defaults.baseURL}/overlay/events/${token}`;
    
    let es;
    const connect = () => {
      es = new EventSource(url);
      es.onopen = () => setSseStatus('connected');
      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === 'media_added' || data.type === 'media_skipped') {
            loadMediaQueue();
          }
          if (data.type === 'subathon_update') {
            loadSubathon();
          }
        } catch {}
      };
      es.onerror = () => {
        setSseStatus('error');
        es.close();
        setTimeout(connect, 3000);
      };
    };

    connect();
    return () => es?.close();
  }, [token, loadMediaQueue, loadSubathon]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await api.put('/overlay/config', {
        alert_duration: form.alert_duration,
        alert_animation: form.alert_animation,
        alert_accent_color: form.alert_accent_color,
        alert_bg_color: form.alert_bg_color,
        alert_text_color: form.alert_text_color,
        alert_font_size: form.alert_font_size,
        ticker_speed: form.ticker_speed,
        subathon_price_per_minute: form.subathon_price_per_minute,
        subathon_title: form.subathon_title,
        media_share_enabled: form.media_share_enabled,
        media_share_min_amount: form.media_share_min_amount,
        media_share_price_per_sec: form.media_share_price_per_sec,
        media_share_max_duration: form.media_share_max_duration,
        media_share_volume: form.media_share_volume,
        leaderboard_count: form.leaderboard_count,
        leaderboard_start_at: form.leaderboard_start_at,
        leaderboard_title: form.leaderboard_title,
        tts_enabled: form.tts_enabled,
        tts_min_amount: form.tts_min_amount,
        tts_volume: form.tts_volume,
        tts_voice: form.tts_voice,
      });
      setConfig(res.data);
      toast.success('Konfigurasi disimpan!');
    } catch (err) {
      toast.error('Gagal menyimpan');
    } finally { setSaving(false); }
  };

  const regenerateToken = async () => {
    if (!window.confirm('Regenerate token? URL overlay lama tidak akan berfungsi lagi.')) return;
    try {
      const res = await api.post('/overlay/token/regenerate');
      setConfig((c) => ({ ...c, overlay_token: res.data.overlay_token }));
      toast.success('Token baru dibuat!');
    } catch { toast.error('Gagal'); }
  };

  const createVote = async () => {
    const opts = voteOptions.split('\n').map(s => s.trim()).filter(Boolean);
    if (!voteTitle.trim() || opts.length < 2) {
      toast.error('Minimal judul dan 2 opsi voting');
      return;
    }
    try {
      await api.post('/overlay/vote', { title: voteTitle, options: opts });
      toast.success('Voting dibuat!');
      setVoteTitle('');
      setVoteOptions('');
      loadVote();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal'); }
  };

  const endVote = async () => {
    if (!activeVote) return;
    try {
      await api.post(`/overlay/vote/${activeVote.vote_id}/end`);
      toast.success('Voting diakhiri');
      setActiveVote(null);
    } catch { toast.error('Gagal'); }
  };

  const startSubathon = async () => {
    try {
      await api.post('/overlay/subathon/start', null, { params: { base_duration: subathonDuration * 60 } });
      setSubathonActive(true);
      toast.success('Subathon dimulai!');
    } catch { toast.error('Gagal'); }
  };

  const stopSubathon = async () => {
    try {
      await api.post('/overlay/subathon/stop');
      setSubathonActive(false);
      toast.success('Subathon dihentikan');
    } catch { toast.error('Gagal'); }
  };

  const skipMedia = async () => {
    try {
      await api.post('/overlay/media/skip');
      toast.success('Media diskip');
      setTimeout(loadMediaQueue, 500);
    } catch { toast.error('Gagal'); }
  };

  const clearMedia = async () => {
    if (!window.confirm('Hapus semua antrian media?')) return;
    try {
      await api.post('/overlay/media/clear');
      toast.success('Antrian dikosongkan');
      setTimeout(loadMediaQueue, 500);
    } catch { toast.error('Gagal'); }
  };

  if (loading) return <div className="p-8 text-center animate-pulse font-bold">Memuat...</div>;

  const baseUrl = `${FRONTEND_URL}/overlay/${token}`;

  const widgets = [
    { key: 'alert', label: 'Alert Box', icon: Broadcast, url: `${baseUrl}/alert?anim=${form.alert_animation || 'slide'}&duration=${form.alert_duration || 5}&accent=${encodeURIComponent(form.alert_accent_color || '#FF6B35')}&tts=${form.tts_enabled || false}&ttsmin=${form.tts_min_amount || 0}&ttsvol=${form.tts_volume || 1.0}&ttsvoice=${form.tts_voice || 'id-ID'}&sound=${encodeURIComponent(form.alert_sound_url || '')}`, desc: 'Notifikasi donasi muncul di layar', recSize: '800 x 600' },
    { key: 'goal', label: 'Donation Goal', icon: ChartBar, url: `${baseUrl}/goal?accent=${encodeURIComponent(form.alert_accent_color || '#FF6B35')}`, desc: 'Progress bar target pendanaan', recSize: '600 x 220' },
    { key: 'ticker', label: 'Running Text', icon: ListNumbers, url: `${baseUrl}/ticker?speed=${form.ticker_speed || 40}&accent=${encodeURIComponent(form.alert_accent_color || '#FF6B35')}`, desc: 'Teks berjalan donatur terbaru', recSize: '1920 x 100' },
    { key: 'subathon', label: 'Subathon Timer', icon: Timer, url: `${baseUrl}/subathon?accent=${encodeURIComponent(form.alert_accent_color || '#FF6B35')}`, desc: 'Timer yang bertambah tiap donasi', recSize: '400 x 230' },
    { key: 'leaderboard', label: 'Leaderboard', icon: ChartBar, url: `${baseUrl}/leaderboard?period=alltime&limit=${form.leaderboard_count || 5}&title=${encodeURIComponent(form.leaderboard_title || 'Top Supporter')}&accent=${encodeURIComponent(form.alert_accent_color || '#FF6B35')}`, desc: 'Top supporter', recSize: '400 x 600' },
    { key: 'media', label: 'Media Share', icon: VideoCamera, url: `${baseUrl}/media?vol=${form.media_share_volume || 1.0}&accent=${encodeURIComponent(form.alert_accent_color || '#FF6B35')}`, desc: 'YouTube, TikTok, IG Reels', recSize: '640 x 640' },
    { key: 'vote', label: 'Voting', icon: CheckSquare, url: `${baseUrl}/vote?accent=${encodeURIComponent(form.alert_accent_color || '#FF6B35')}`, desc: 'Polling real-time', recSize: '430 x 500' },
    { key: 'qr', label: 'QR Code', icon: QrCode, url: `${baseUrl}/qr?accent=${encodeURIComponent(form.alert_accent_color || '#FF6B35')}&label=${encodeURIComponent(qrLabel)}`, desc: 'Scan untuk traktir langsung', recSize: '340 x 320' },
  ];

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-bold mb-1">Live Streaming</p>
          <h2 className="font-display text-3xl font-bold tracking-tight">Overlay OBS</h2>
          <p className="text-sm text-muted-foreground mt-1">Tambahkan widget di OBS/Streamlabs via Browser Source</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 border-2 border-foreground font-bold text-[10px] uppercase shadow-brutal-sm ${
          sseStatus === 'connected' ? 'bg-green-500 text-white' : sseStatus === 'error' ? 'bg-destructive text-white' : 'bg-accent'
        }`}>
          <div className={`w-2 h-2 rounded-full animate-pulse bg-white`} />
          {sseStatus === 'connected' ? 'SSE Connected' : sseStatus === 'error' ? 'SSE Disconnected' : 'Connecting SSE...'}
        </div>
      </div>

      {/* TOKEN */}
      <div className="border-2 border-foreground bg-white shadow-brutal-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold">Token Overlay</h3>
          <button onClick={regenerateToken} className="inline-flex items-center gap-1 border-2 border-foreground bg-accent px-2 py-1 text-[10px] font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]">
            <ArrowsClockwise size={10} weight="bold" /> Regenerate
          </button>
        </div>
        <div className="flex gap-2 items-center">
          <code className="flex-1 border-2 border-foreground bg-background px-3 py-2 text-xs font-mono break-all">{token}</code>
          <CopyButton text={token} label="Token" />
        </div>
        <p className="text-[10px] text-muted-foreground mt-2 italic">⚠️ Jangan bagikan token ini ke publik. Regenerate jika bocor.</p>
      </div>

      {/* WIDGET URLS */}
      <div className="border-2 border-foreground bg-white shadow-brutal-md p-5">
        <h3 className="font-display text-lg font-bold mb-4">Widget URLs (untuk OBS Browser Source)</h3>
        <div className="space-y-3">
          {widgets.map((w) => (
            <div key={w.key} className="border-2 border-foreground bg-background p-3">
              <div className="flex items-center gap-2 mb-1">
                <w.icon size={16} weight="bold" />
                <span className="font-bold text-sm">{w.label}</span>
                <span className="text-[10px] text-primary font-mono ml-2">({w.recSize})</span>
                <span className="text-[10px] text-muted-foreground ml-auto">{w.desc}</span>
              </div>
              <div className="flex gap-2 items-center">
                <code className="flex-1 text-[10px] font-mono bg-white border border-foreground/20 px-2 py-1 truncate">{w.url}</code>
                <CopyButton text={w.url} label={w.label} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 border-2 border-foreground bg-secondary/30 p-3">
          <p className="text-xs font-bold mb-1">📋 Cara Pasang di OBS:</p>
          <ol className="text-[10px] text-muted-foreground space-y-0.5 list-decimal ml-4">
            <li>Buka OBS → Sources → + → Browser Source</li>
            <li>Paste URL widget di atas</li>
            <li><strong>Set Width & Height</strong> sesuai rekomendasi di samping nama widget (misal: 800x600)</li>
            <li>Centang "Shutdown source when not visible"</li>
            <li>Centang "Refresh browser when scene becomes active"</li>
          </ol>
        </div>
      </div>

      {/* ALERT SETTINGS */}
      <div className="border-2 border-foreground bg-white shadow-brutal-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sliders size={20} weight="bold" />
            <h3 className="font-display text-lg font-bold">Pengaturan Alert</h3>
          </div>
          <button 
            disabled={testingAlert}
            onClick={async () => {
              setTestingAlert(true);
              try {
                await api.post('/overlay/test-alert');
                toast.success('Test alert dikirim!');
              } catch { toast.error('Gagal kirim test alert'); }
              finally { setTestingAlert(false); }
            }} 
            className="inline-flex items-center gap-1 border-2 border-foreground bg-accent px-3 py-1.5 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-50"
          >
            <Play size={12} weight="bold" /> {testingAlert ? 'Mengirim...' : 'Test Alert'}
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Animasi</label>
            <select value={form.alert_animation || 'slide'} onChange={(e) => setForm({ ...form, alert_animation: e.target.value })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm">
              <option value="slide">Slide</option>
              <option value="bounce">Bounce</option>
              <option value="fade">Fade</option>
              <option value="zoom">Zoom</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Durasi (detik)</label>
            <input type="number" min="2" max="30" value={form.alert_duration || 5} onChange={(e) => setForm({ ...form, alert_duration: parseInt(e.target.value || '5') })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Font Size</label>
            <input type="number" min="16" max="64" value={form.alert_font_size || 32} onChange={(e) => setForm({ ...form, alert_font_size: parseInt(e.target.value || '32') })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Warna Aksen</label>
            <input type="color" value={form.alert_accent_color || '#FF6B35'} onChange={(e) => setForm({ ...form, alert_accent_color: e.target.value })} className="w-full h-10 border-2 border-foreground cursor-pointer" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Warna Teks</label>
            <input type="color" value={form.alert_text_color || '#FFFFFF'} onChange={(e) => setForm({ ...form, alert_text_color: e.target.value })} className="w-full h-10 border-2 border-foreground cursor-pointer" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Leaderboard Limit</label>
            <input type="number" min="3" max="20" value={form.leaderboard_count || 5} onChange={(e) => setForm({ ...form, leaderboard_count: parseInt(e.target.value || '5') })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Leaderboard Title</label>
            <input value={form.leaderboard_title || 'Top Supporter'} onChange={(e) => setForm({ ...form, leaderboard_title: e.target.value })} placeholder="Top Supporter" className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Leaderboard Start (Reset)</label>
            <input type="datetime-local" 
              value={form.leaderboard_start_at ? new Date(new Date(form.leaderboard_start_at).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''} 
              onChange={(e) => setForm({ ...form, leaderboard_start_at: e.target.value || null })} 
              className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Harga (Rp) untuk +1 Menit</label>
            <input type="number" min="100" step="100" value={form.subathon_price_per_minute || 10000} onChange={(e) => setForm({ ...form, subathon_price_per_minute: parseInt(e.target.value || '10000') })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Judul Subathon</label>
            <input type="text" value={form.subathon_title || 'Subathon'} onChange={(e) => setForm({ ...form, subathon_title: e.target.value })} placeholder="Subathon" className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Ticker Speed</label>
            <input type="number" min="10" max="100" value={form.ticker_speed || 40} onChange={(e) => setForm({ ...form, ticker_speed: parseInt(e.target.value || '40') })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
        </div>

        <div className="mt-6 border-t-2 border-foreground/10 pt-4">
          <div className="flex items-center gap-2 mb-4">
            <MusicNote size={20} weight="bold" />
            <h3 className="font-display text-lg font-bold">Alert & Sound</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Alert Sound URL (MP3/OGG)</label>
              <input 
                value={form.alert_sound_url || ''} 
                onChange={(e) => setForm({ ...form, alert_sound_url: e.target.value })} 
                placeholder="https://example.com/sound.mp3"
                className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" 
              />
              <p className="text-[9px] mt-1 text-foreground/60 italic">Kosongkan untuk suara default (Ding!)</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Animasi Alert</label>
              <select value={form.alert_animation || 'slide'} onChange={(e) => setForm({ ...form, alert_animation: e.target.value })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm">
                <option value="slide">Slide</option>
                <option value="bounce">Bounce</option>
                <option value="fade">Fade</option>
                <option value="zoom">Zoom</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t-2 border-foreground/10 pt-4">
          <div className="flex items-center gap-2 mb-4">
            <MicrophoneStage size={20} weight="bold" />
            <h3 className="font-display text-lg font-bold">Text to Speech (TTS)</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 md:col-span-1">
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1 text-primary">Status TTS</label>
              <div className="flex items-center gap-2 mt-2">
                <input 
                  type="checkbox" 
                  checked={form.tts_enabled || false} 
                  onChange={(e) => setForm({ ...form, tts_enabled: e.target.checked })}
                  className="w-5 h-5 border-2 border-foreground accent-primary"
                />
                <span className="text-sm font-bold">{form.tts_enabled ? 'Aktif' : 'Nonaktif'}</span>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Min. Donasi (Rp)</label>
              <input type="number" step="1000" value={form.tts_min_amount || 0} onChange={(e) => setForm({ ...form, tts_min_amount: parseInt(e.target.value || '0') })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Volume (0-1)</label>
              <input type="number" step="0.1" min="0" max="1" value={form.tts_volume || 1.0} onChange={(e) => setForm({ ...form, tts_volume: parseFloat(e.target.value || '1.0') })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Bahasa / Suara</label>
              <select value={form.tts_voice || 'id-ID'} onChange={(e) => setForm({ ...form, tts_voice: e.target.value })} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm">
                <option value="id-ID">Indonesia (Mbak)</option>
                <option value="en-US">English (US)</option>
                <option value="ja-JP">Japanese</option>
              </select>
            </div>
          </div>
        </div>
        <button onClick={saveConfig} disabled={saving} className="mt-4 inline-flex items-center gap-2 border-2 border-foreground bg-primary text-white px-5 py-2 font-bold uppercase text-xs tracking-wide shadow-brutal hover:shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] disabled:opacity-60">
          {saving ? 'Menyimpan...' : 'Simpan Pengaturan'}
        </button>
        <p className="mt-4 text-[11px] bg-accent/30 border-l-4 border-accent p-3 font-bold italic leading-relaxed">
          💡 Catatan: Setiap kali Anda mengubah pengaturan di atas (terutama tulisan QR Code, warna, atau durasi), silakan klik "Simpan Pengaturan" lalu salin (copy) ulang link widget-nya ke OBS agar tampilan terbaru langsung terupdate.
        </p>
      </div>

      {/* SUBATHON CONTROL */}
      <div className="border-2 border-foreground bg-white shadow-brutal-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <Timer size={20} weight="bold" />
          <h3 className="font-display text-lg font-bold">Subathon Control</h3>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Durasi Awal (menit)</label>
            <input type="number" min="1" max="1440" value={subathonDuration} onChange={(e) => setSubathonDuration(parseInt(e.target.value || '60'))} className="w-32 border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
          {subathonActive ? (
            <button onClick={stopSubathon} className="inline-flex items-center gap-1 border-2 border-foreground bg-destructive text-white px-5 py-2 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]">
              <Stop size={12} weight="bold" /> Stop Subathon
            </button>
          ) : (
            <button onClick={startSubathon} className="inline-flex items-center gap-1 border-2 border-foreground bg-secondary px-5 py-2 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]">
              <Play size={12} weight="bold" /> Start Subathon
            </button>
          )}
        </div>
      </div>

      {/* VOTING CONTROL */}
      <div className="border-2 border-foreground bg-white shadow-brutal-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <CheckSquare size={20} weight="bold" />
          <h3 className="font-display text-lg font-bold">Voting</h3>
        </div>
        {activeVote ? (
          <div className="border-2 border-foreground bg-accent/20 p-4 mb-4">
            <p className="font-bold text-sm mb-2">Voting Aktif: {activeVote.title}</p>
            <div className="space-y-1">
              {activeVote.options?.map((o, i) => (
                <p key={i} className="text-xs">#{i + 1} {o.label}: <strong>{o.votes} votes</strong></p>
              ))}
            </div>
            <button onClick={endVote} className="mt-3 inline-flex items-center gap-1 border-2 border-foreground bg-destructive text-white px-3 py-1.5 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]">
              <Stop size={10} weight="bold" /> Akhiri Voting
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Judul Voting</label>
              <input value={voteTitle} onChange={(e) => setVoteTitle(e.target.value)} placeholder="Misal: Besok main game apa?" className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Opsi (1 per baris)</label>
              <textarea value={voteOptions} onChange={(e) => setVoteOptions(e.target.value)} rows={3} placeholder={"Horror\nFPS\nRPG"} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm resize-none" />
            </div>
            <button onClick={createVote} className="inline-flex items-center gap-1 border-2 border-foreground bg-primary text-white px-3 py-2 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]">
              <Play size={10} weight="bold" /> Buat Voting
            </button>
            <p className="text-[10px] text-muted-foreground italic">Donatur mengirim pesan #1, #2, dst saat traktir untuk memberikan vote.</p>
          </div>
        )}
      </div>

      {/* MEDIA SHARE CONTROL & SETTINGS */}
      <div className="border-2 border-foreground bg-white shadow-brutal-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <VideoCamera size={20} weight="bold" />
            <h3 className="font-display text-lg font-bold">Media Share</h3>
            <div className="ml-4 flex items-center gap-2 bg-secondary/30 px-3 py-1 border-2 border-foreground/10 rounded-lg">
              <input 
                type="checkbox" 
                id="media_enabled_toggle"
                checked={form.media_share_enabled || false} 
                onChange={(e) => setForm({...form, media_share_enabled: e.target.checked})}
                className="w-5 h-5 accent-primary cursor-pointer border-2 border-foreground" 
              />
              <label htmlFor="media_enabled_toggle" className="text-[11px] font-black uppercase cursor-pointer select-none">
                {form.media_share_enabled ? '🟢 AKTIF' : '⚪ NONAKTIF'}
              </label>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={saveConfig} className="border-2 border-foreground bg-primary text-white px-3 py-1.5 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px]">
              Simpan
            </button>
          </div>
        </div>

        {/* MEDIA SHARE TEST PANEL */}
        <div className="border-2 border-foreground bg-accent/10 p-4 space-y-3 mt-4">
          <p className="text-xs font-bold uppercase tracking-widest flex items-center gap-2">🧪 Test Media Share</p>
          <p className="text-[10px] text-muted-foreground">Tes video tanpa donasi asli. Paste link atau klik tombol cepat di bawah.</p>
          
          <div className="flex gap-2">
            <input
              value={testMediaUrl}
              onChange={(e) => setTestMediaUrl(e.target.value)}
              placeholder="Paste link YouTube atau TikTok..."
              className="flex-1 border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm focus:outline-none focus:border-primary"
            />
            <button 
              disabled={testingMedia || !testMediaUrl.trim()}
              onClick={async () => {
                setTestingMedia(true);
                try {
                  await api.post(`/overlay/test-media?url=${encodeURIComponent(testMediaUrl.trim())}`);
                  toast.success('Test Media dikirim ke overlay!');
                } catch { toast.error('Gagal kirim test media'); }
                finally { setTestingMedia(false); }
              }} 
              className="border-2 border-foreground bg-primary text-white px-4 py-2 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-40 whitespace-nowrap"
            >
              <Play size={12} weight="bold" className="inline mr-1 -mt-0.5" /> 
              {testingMedia ? 'Kirim...' : 'Kirim'}
            </button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {[
              { label: '▶️ YouTube', url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ' },
              { label: '🎵 TikTok', url: 'https://vt.tiktok.com/ZS9CByUHU/' },
            ].map((item) => (
              <button
                key={item.label}
                disabled={testingMedia}
                onClick={async () => {
                  setTestingMedia(true);
                  setTestMediaUrl(item.url);
                  try {
                    await api.post(`/overlay/test-media?url=${encodeURIComponent(item.url)}`);
                    toast.success(`Test ${item.label} dikirim!`);
                  } catch { toast.error('Gagal kirim test'); }
                  finally { setTestingMedia(false); }
                }}
                className="border-2 border-foreground bg-white px-3 py-2 text-xs font-bold shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] hover:bg-accent disabled:opacity-40 transition-all"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Jumlah Minimum</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">Rp</span>
              <input type="number" value={form.media_share_min_amount || 0} onChange={(e) => setForm({...form, media_share_min_amount: parseInt(e.target.value)})} className="w-full border-2 border-foreground bg-white pl-10 pr-3 py-2 text-sm shadow-brutal-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Harga per Detik</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">Rp</span>
              <input type="number" value={form.media_share_price_per_sec || 0} onChange={(e) => setForm({...form, media_share_price_per_sec: parseInt(e.target.value)})} className="w-full border-2 border-foreground bg-white pl-10 pr-3 py-2 text-sm shadow-brutal-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Durasi Maksimal (Detik)</label>
            <input type="number" value={form.media_share_max_duration || 300} onChange={(e) => setForm({...form, media_share_max_duration: parseInt(e.target.value)})} className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Volume (%) - {Math.round((form.media_share_volume || 1) * 100)}%</label>
            <input type="range" min="0" max="1" step="0.05" value={form.media_share_volume || 1} onChange={(e) => setForm({...form, media_share_volume: parseFloat(e.target.value)})} className="w-full h-10 accent-primary" />
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={skipMedia} disabled={mediaQueueCount === 0} className="inline-flex items-center gap-1 border-2 border-foreground bg-accent px-3 py-2 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-30 disabled:grayscale">
            Skip Video
          </button>
          <button onClick={clearMedia} disabled={mediaQueueCount === 0} className="inline-flex items-center gap-1 border-2 border-foreground bg-destructive text-white px-3 py-2 text-xs font-bold uppercase shadow-brutal-sm hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] disabled:opacity-30 disabled:grayscale">
            Kosongkan Antrian
          </button>
          {mediaQueueCount > 0 && <span className="ml-auto self-center text-xs font-bold bg-primary text-white px-2 py-1 border-2 border-foreground">{mediaQueueCount} Video Antri</span>}
        </div>
        <div className="mt-4 p-3 bg-primary/5 border-2 border-foreground/10 text-[11px] leading-relaxed">
          <p className="font-bold text-primary mb-1">📢 Info Harga untuk Donatur:</p>
          <ul className="list-disc ml-4 space-y-0.5">
            <li>Minimal donasi untuk putar video: <strong>Rp {form.media_share_min_amount?.toLocaleString()}</strong></li>
            <li>Tarif video: <strong>Rp {form.media_share_price_per_sec?.toLocaleString()} per detik</strong></li>
            <li>Durasi maksimal video: <strong>{form.media_share_max_duration} detik</strong></li>
          </ul>
          <p className="font-bold text-primary mt-3 mb-1">📐 Rekomendasi Ukuran OBS:</p>
          <p className="ml-1">Gunakan <strong>1 Browser Source</strong> dengan ukuran <strong>640 × 660</strong>. Overlay otomatis menyesuaikan layout: landscape untuk YouTube, portrait untuk TikTok.</p>
          <p className="mt-2 text-muted-foreground italic">Platform yang didukung: YouTube, TikTok. Donatur cukup paste link-nya saja.</p>
        </div>
      </div>

      {/* QR CODE PREVIEW & SETTINGS */}
      <div className="border-2 border-foreground bg-white shadow-brutal-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <QrCode size={20} weight="bold" />
          <h3 className="font-display text-lg font-bold">QR Code Overlay</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Settings */}
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Label Teks</label>
              <input
                value={qrLabel}
                onChange={(e) => setQrLabel(e.target.value)}
                placeholder="TRAKTIR DI SINI"
                className="w-full border-2 border-foreground bg-white px-3 py-2 text-sm shadow-brutal-sm"
              />
              <p className="text-[10px] text-muted-foreground mt-1 italic">Teks yang muncul di atas QR code.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold block mb-1">Warna Aksen</label>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border-2 border-foreground" style={{ background: form.alert_accent_color || '#FF6B35' }} />
                <span className="text-xs font-mono">{form.alert_accent_color || '#FF6B35'}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 italic">Diambil dari Warna Aksen di pengaturan alert di atas.</p>
            </div>
            <div className="border-2 border-foreground bg-secondary/30 p-3">
              <p className="text-xs font-bold mb-1">🖥️ Ukuran yang disarankan:</p>
              <p className="text-[10px] text-muted-foreground">Width: 350 × Height: 420</p>
            </div>
          </div>

          {/* Live Preview */}
          <div className="flex flex-col items-center justify-center">
            <p className="text-[10px] uppercase tracking-widest font-bold mb-3">Preview</p>
            <div className="inline-flex flex-col items-stretch border-[3px] border-black shadow-brutal-md bg-white overflow-hidden" style={{ maxWidth: 200 }}>
              {/* Label */}
              <div className="py-2.5 px-4 text-center border-b-[3px] border-black" style={{ background: form.alert_accent_color || '#FF6B35' }}>
                <span className="text-white font-black text-[12px] uppercase tracking-wider">
                  {qrLabel}
                </span>
              </div>
              {/* QR */}
              <div className="p-4 bg-white flex items-center justify-center">
                <div className="w-32 h-32 bg-muted flex items-center justify-center text-[10px] text-muted-foreground font-bold uppercase text-center p-4">
                  QR Preview
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
